import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import { useHistory, useLocation, useParams } from "react-router";
import { FilterChipComponentRef } from "entity-app/shared-components/log-render-components/filter-chip-component";
import { FeatureTypes } from "entity-app/constants";

import { updateGridSelectionOnFilter } from "utils/utils";
import { RowStyle } from "ag-grid-enterprise";
import { useMaterialLogData } from "./hooks/material-feature-data";

import { useMaterialLogAgGrid } from "./hooks/material-ag-grid-data";
import MaterialHeaderItems from "./components/header-items";
import { MaterialExtractSpecType } from "./model";
import ExtractedPdfViewer from "./components/extracted-pdf-viewer";

function MaterialExtractedLogPage() {
  const { state } = useLocation() as any;
  const history = useHistory();

  const gridRef = useRef<AgGridReact<any>>(null);
  const [isGridReady, setGridReady] = useState(false);
  const [selectedExtraction, setExtraction] =
    useState<MaterialExtractSpecType>();

  const pageData = useMaterialLogData();
  const { setEntityList, entityList } = pageData;
  const { projectId } = useParams() as any;

  const agGridData = useMaterialLogAgGrid({
    pageData
  });

  const filterChipRef = useRef<FilterChipComponentRef>(null);

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  useEffect(() => {
    const extractionId = state?.extraction?.id;

    if (!extractionId || !entityList?.length) return;

    const rowData = entityList.find((row) => row.id === extractionId);
    if (!rowData) return;

    setExtraction(rowData);

    setEntityList((prev) =>
      prev
        ?.map((row) => ({
          ...row,
          active:
            row.id === extractionId || row.grouped_parent_id === extractionId
        }))
        .sort((a, b) => Number(b.active) - Number(a.active))
    );

    setTimeout(() => {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.forEachNode((node) => {
          node.setSelected(node.data.active);
        });
      }
    }, 500);

    // Clear history state once used
    history.replace(history.location.pathname, {});
  }, [state?.extraction?.id, entityList, setEntityList, history]);

  // Listen for changes in the "active" field
  const onCellValueChanged = (params: any) => {
    if (params.colDef.field === "active") {
      // Update state so React re-renders (if you want controlled rowData)
      pageData.setEntityList((prev) => {
        if (!prev) return prev;
        return prev.map((row) =>
          row.id === params.data.id
            ? { ...row, active: params.data.active }
            : row
        );
      });
    }
  };

  const getRowStyle = useCallback(
    (params): RowStyle | undefined => {
      if (params.data.id === selectedExtraction?.id) {
        return {
          backgroundColor: "var(--ciq-blue-100)"
        };
      }
      return undefined;
    },
    [selectedExtraction?.id]
  );

  useEffect(() => {
    if (!selectedExtraction && gridRef?.current?.api)
      gridRef?.current?.api?.sizeColumnsToFit();
    setTimeout(() => {
      gridRef?.current?.api?.redrawRows();
    });
  }, [selectedExtraction]);

  useEffect(() => {
    if (gridRef.current && pageData.entityList && selectedExtraction?.id) {
      const api = gridRef.current?.api;
      const rowNode = api?.getRowNode(selectedExtraction.id);
      if (rowNode) {
        api?.ensureNodeVisible(rowNode, "middle"); // "top" | "bottom" | "middle"
      }
    }
  }, [pageData.entityList, selectedExtraction?.id]);

  return (
    <div className="flex flex-col h-full w-full">
      <MaterialHeaderItems
        gridRef={gridRef}
        pageData={pageData}
        filterChipRef={filterChipRef}
        statsData={statsData}
        projectId={projectId}
        isGridReady={isGridReady}
      />
      <div className="flex h-full w-full">
        <div
          className="flex flex-col h-full"
          style={{ width: selectedExtraction ? 460 : "100%" }}
        >
          <AgGridReact<MaterialExtractSpecType>
            ref={gridRef}
            className="ag-theme-alpine"
            rowData={pageData.entityList}
            columnDefs={agGridData.columnDefs}
            defaultColDef={agGridData.defaultColDef}
            rowHeight={62}
            onFirstDataRendered={(e) => {
              setGridReady(true);
              e.api.sizeColumnsToFit();
            }}
            onCellValueChanged={onCellValueChanged}
            getRowId={(param) => param.data.id}
            getRowStyle={getRowStyle}
            rowSelection={{ mode: "multiRow", selectAll: "filtered" }}
            onCellClicked={(e) => {
              if (e.colDef.colId !== "ag-Grid-SelectionColumn") {
                setExtraction(e.data);
              }
            }}
            onSelectionChanged={() => {
              const selectedRows =
                gridRef?.current?.api?.getSelectedRows() as MaterialExtractSpecType[];
              pageData.setEntityList((prev) =>
                prev?.map((row) => {
                  return {
                    ...row,
                    active: selectedRows.some((selRow) => selRow.id === row.id)
                  };
                })
              );
            }}
            onFilterChanged={(event: any) => {
              filterChipRef.current?.onFilterApplied();
              setStatsData(
                calculateLogPageStats({
                  gridRef,
                  ListFilter: undefined,
                  featureTypeId: FeatureTypes.MATERIALS
                })
              );

              if (event.source === "quickFilter") return;
              updateGridSelectionOnFilter(event, gridRef);
            }}
          />
        </div>
        {selectedExtraction && (
          <ExtractedPdfViewer
            extrationData={selectedExtraction}
            onClose={() => {
              setExtraction(undefined);
              gridRef.current?.api.deselectAll();
            }}
          />
        )}
      </div>
    </div>
  );
}

export default MaterialExtractedLogPage;
